import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const PreferencesRequest = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    // 1. Pre-fill email if user ID is provided in URL (highest priority)
    const userRef = searchParams.get("ref");
    if (userRef) {
      fetchEmailByRef(userRef);
      return;
    }

    // 2. Pre-fill email if provided directly in URL (e.g., from session expiry redirect)
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
      return;
    }

    // 3. Try to get email from newsletter_session localStorage
    const storedSession = localStorage.getItem('newsletter_session');
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);
        if (sessionData.email) {
          setEmail(sessionData.email);
          return;
        }
      } catch (e) {
        console.error('Error parsing newsletter_session:', e);
      }
    }

    // 3. Try to get email from funnel_session localStorage
    const funnelSession = localStorage.getItem('funnel_session');
    if (funnelSession) {
      try {
        const data = JSON.parse(funnelSession);
        if (data.email) {
          setEmail(data.email);
          return;
        }
      } catch (e) {
        console.error('Error parsing funnel_session:', e);
      }
    }

    // 4. Try to get email from comeback_prospect localStorage
    const comebackProspect = localStorage.getItem('comeback_prospect');
    if (comebackProspect) {
      try {
        const data = JSON.parse(comebackProspect);
        if (data.email) {
          setEmail(data.email);
          return;
        }
      } catch (e) {
        console.error('Error parsing comeback_prospect:', e);
      }
    }
  }, [searchParams]);

  const fetchEmailByRef = async (userRef: string) => {
    try {
      const { data, error } = await supabase
        .from('newsletter_subscriptions')
        .select('email')
        .eq('id', userRef)
        .single();

      if (!error && data) {
        setEmail(data.email);
      }
    } catch (error) {
      console.error('Error fetching email by ref:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: t("preferencesRequest.errors.errorTitle"),
        description: t("preferencesRequest.errors.invalidEmail"),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-magic-link', {
        body: { email: email.toLowerCase().trim() }
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        toast({
          title: t("preferencesRequest.errors.errorTitle"),
          description: data.error,
          variant: "destructive"
        });
        return;
      }

      setEmailSent(true);
      toast({
        title: t("preferencesRequest.errors.successTitle"),
        description: t("preferencesRequest.errors.successDescription")
      });

    } catch (error: any) {
      console.error('Error generating magic link:', error);
      toast({
        title: t("preferencesRequest.errors.errorTitle"),
        description: error.message || t("preferencesRequest.errors.sendError"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-newsletter-light via-background to-newsletter-light/50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">{t("preferencesRequest.success.title")}</CardTitle>
            <CardDescription
              dangerouslySetInnerHTML={{
                __html: t("preferencesRequest.success.description", { email })
              }}
            />
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setEmailSent(false);
                setEmail("");
              }}
              className="w-full"
            >
              {t("preferencesRequest.success.sendToAnother")}
            </Button>
            <Link to="/">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("preferencesRequest.success.backHome")}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-newsletter-light via-background to-newsletter-light/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">{t("preferencesRequest.access.title")}</CardTitle>
          <CardDescription className="text-center">
            {t("preferencesRequest.access.description")}
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("preferencesRequest.access.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("preferencesRequest.access.emailPlaceholder")}
                required
                disabled={isLoading}
              />
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("preferencesRequest.access.sendingButton")}
                </>
              ) : (
                t("preferencesRequest.access.sendButton")
              )}
            </Button>
            
            <Link to="/">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("preferencesRequest.success.backHome")}
              </Button>
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default PreferencesRequest;